import { formatDistanceToNow } from "date-fns"
import { useNavigate, useLocation } from "react-router-dom"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Bell } from "@solar-icons/react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useAuthStore } from "@/stores/auth"
import {
  useGetNotificationsQuery,
  useGetNotificationsUnreadCountQuery,
  useMarkNotificationReadMutation,
} from "@/queries/notifications"
import { resolveNotificationPath } from "@/lib/notification-navigation"
import type { InboxNotification } from "@/api/endpoints/notifications"
import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"

const PREVIEW_LIMIT = 5

export function HeaderNotificationsMenu() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation(["common", "settings"])
  const notificationsPath = location.pathname.startsWith("/admin")
    ? "/admin/settings/notifications"
    : "/settings/notifications"
  const token = useAuthStore((state) => state.token)
  const { data: unreadCount = 0 } = useGetNotificationsUnreadCountQuery({
    enabled: !!token,
  })
  const { data, isLoading } = useGetNotificationsQuery(
    { page: 1, pageSize: PREVIEW_LIMIT },
    { enabled: !!token },
  )
  const { mutateAsync: markRead } = useMarkNotificationReadMutation()

  const notifications = data?.data ?? []
  const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount)

  const handleNotificationClick = async (notification: InboxNotification) => {
    if (!notification.readAt) {
      try {
        await markRead(notification._id)
      } catch {
        // Non-blocking for header preview navigation
      }
    }

    const destination = resolveNotificationPath(notification)
    if (destination) {
      navigate(destination)
      return
    }

    navigate(notificationsPath)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          size="icon"
          className="relative h-9 w-9"
          data-tour-step-id="header-notifications"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className={cn(
                "absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center px-1 text-xs",
                unreadCount > 9 && "px-1.5",
              )}
            >
              {badgeLabel}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>{t("notifications.title", { ns: "settings" })}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isLoading ? (
          <div className="py-8 text-center text-xs text-muted-foreground">
            {t("notifications.updating", { ns: "settings" })}
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-8 text-center text-xs text-muted-foreground">
            {t("notifications.emptyTitle", { ns: "settings" })}
          </div>
        ) : (
          notifications.map((notification) => (
            <DropdownMenuItem
              key={notification._id}
              className={cn(
                "flex flex-col items-start gap-1 py-3",
                !notification.readAt && "bg-primary/5",
                notification.type === "SLOT_REQUEST_CREATED" && "bg-primary/5",
              )}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="flex items-center justify-between w-full gap-2">
                <span
                  className={cn(
                    "text-sm line-clamp-1",
                    !notification.readAt ? "font-semibold" : "font-medium",
                  )}
                >
                  {notification.title?.trim() || "Notification"}
                </span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {notification.createdAt
                    ? formatDistanceToNow(new Date(notification.createdAt), {
                        addSuffix: true,
                      })
                    : ""}
                </span>
              </div>
              <span className="text-xs text-muted-foreground line-clamp-2">
                {notification.body}
              </span>
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="justify-center text-center py-2"
          onClick={() => navigate(notificationsPath)}
        >
          <span className="text-xs text-muted-foreground">
            {t("actions.viewAll", { ns: "common" })}
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
